import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import DramaCard from '@/components/base/DramaCard';
import { genres, years, dramas } from '@/mocks/dramas';
import { searchDramas, sortDramas } from '@/utils/drama';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [keyword, setKeyword] = useState(query);
  const [genre, setGenre] = useState('');
  const [year, setYear] = useState<number | ''>('');

  useEffect(() => {
    setKeyword(query);
    setGenre('');
    setYear('');
  }, [query]);

  const results = useMemo(() => {
    let list = query.trim() ? searchDramas(query) : [];
    if (genre) list = list.filter((d) => d.genres.includes(genre));
    if (year) list = list.filter((d) => d.year === year);
    return sortDramas(list, 'hot');
  }, [query, genre, year]);

  const hotPicks = sortDramas(dramas, 'hot').slice(0, 4);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setSearchParams(q ? { q } : {});
  };

  const pill = (active: boolean) =>
    `whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors cursor-pointer ${
      active
        ? 'border-primary-500 bg-primary-500 text-background-50'
        : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300'
    }`;

  return (
    <SiteLayout>
      <title>{query ? `搜索“${query}” - 善良的小姨子在线` : '搜索剧集 - 善良的小姨子在线'}</title>
      <meta name="description" content="在善良的小姨子在线搜索你想看的电视剧，支持按剧名、主演、类型关键词查找，快速定位目标内容。" />
      <meta name="keywords" content="电视剧搜索,剧名搜索,主演搜索,善良的小姨子在线" />

      <div className="bg-background-100 border-b border-background-200">
        <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <nav className="flex items-center gap-1 text-sm text-foreground-600" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-900">搜索</span>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            搜索剧集
          </h1>
          <p className="mt-2 text-sm text-foreground-600">支持按剧名、主演、类型与地区关键词查找</p>

          <form onSubmit={handleSubmit} className="mt-5 flex max-w-2xl flex-col gap-2 sm:flex-row">
            <div className="flex flex-1 items-center overflow-hidden rounded-md border border-background-300 bg-background-50">
              <span className="flex h-11 w-11 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-lg"></i>
              </span>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入剧名或主演，例如：张译"
                className="h-11 w-full bg-transparent pr-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-search-2-line"></i>
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-8 md:px-6">
        {query.trim() ? (
          <>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-700">
                关键词 <span className="font-semibold text-primary-600">“{query}”</span> 共找到{' '}
                <span className="font-semibold text-primary-600">{results.length}</span> 个结果
              </p>
            </div>

            <div className="mb-6 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center">
              <span className="text-sm font-medium text-foreground-700">筛选</span>
              <div className="flex flex-wrap gap-2">
                <button type="button" className={pill(genre === '')} onClick={() => setGenre('')}>全部类型</button>
                {genres.slice(0, 8).map((g) => (
                  <button key={g} type="button" className={pill(genre === g)} onClick={() => setGenre(g)}>{g}</button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 md:ml-auto">
                <button type="button" className={pill(year === '')} onClick={() => setYear('')}>全部年份</button>
                {years.map((y) => (
                  <button key={y} type="button" className={pill(year === y)} onClick={() => setYear(y)}>{y}</button>
                ))}
              </div>
            </div>

            {results.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5">
                {results.map((d, i) => (
                  <DramaCard key={d.id} drama={d} index={i} showSynopsis />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-emotion-sad-line text-3xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                  没有找到与“{query}”相关的剧集
                </h2>
                <p className="mt-2 max-w-md text-sm text-foreground-600">
                  换个关键词试试，或者看看下面的热门推荐，说不定正好有你喜欢的。
                </p>
              </div>
            )}

            {results.length === 0 && (
              <div className="mt-8">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                  <i className="ri-fire-fill text-primary-500"></i>
                  热门推荐
                </h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5">
                  {hotPicks.map((d, i) => (
                    <DramaCard key={d.id} drama={d} index={i} />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div>
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                输入关键词开始搜索
              </h2>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                你可以搜索剧名，也可以搜索主演、类型或地区，快速找到想看的电视剧。
              </p>
            </div>
            <div className="mt-8">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                <i className="ri-fire-fill text-primary-500"></i>
                大家在追
              </h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5">
                {hotPicks.map((d, i) => (
                  <DramaCard key={d.id} drama={d} index={i} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}